// Notification WebSocket provider following existing provider patterns
import React, { useEffect, useState, createContext, useContext, ReactNode } from 'react';
import notificationSocketService, { NotificationConnectionState } from '@/api/notification-socket-service';
import { useKeycloak } from '@react-keycloak/web';
import { useOrganization } from '@/contexts/organization-context';
import { NotificationData } from '@/api/notification-service';

// Connection info type for debugging
interface ConnectionInfo {
  state: NotificationConnectionState;
  isSocketConnected: boolean;
  reconnectAttempts: number;
  lastPongTime: number;
  missedPongs: number;
  connectionAge: number;
}

// Create context to share notification WebSocket state
interface NotificationWebSocketContextType {
  isConnected: boolean;
  connectionState: NotificationConnectionState;
  notifications: NotificationData[];
  unreadCount: number;
  connect: () => Promise<void>;
  forceReconnect: () => Promise<void>;
  markAsRead: (notificationId: string) => boolean;
  markAllAsRead: () => void;
  getNotifications: (limit?: number, offset?: number) => boolean;
  clearNotifications: () => void;
  getConnectionInfo: () => ConnectionInfo;
}

const NotificationWebSocketContext = createContext<NotificationWebSocketContextType | undefined>(undefined);

// Provider component
export const NotificationWebSocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { keycloak, initialized } = useKeycloak();
  const { currentOrganization, currentInstitution } = useOrganization();
  
  // State management
  const [connectionState, setConnectionState] = useState<NotificationConnectionState>(NotificationConnectionState.DISCONNECTED);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [connectionInitialized, setConnectionInitialized] = useState(false);
  
  // Derived state
  const isConnected = connectionState === NotificationConnectionState.CONNECTED;
  const unreadCount = notifications.filter(n => !n.readAt).length;

  // Update organization context when it changes
  useEffect(() => {
    console.log('NotificationWebSocketProvider: Organization context changed', {
      organizationId: currentOrganization?.id,
      institutionId: currentInstitution?.id
    });
    
    notificationSocketService.setOrganizationContext(
      currentOrganization?.id || null,
      currentInstitution?.id || null
    );
  }, [currentOrganization?.id, currentInstitution?.id, initialized]);

  // Set up event listeners and initialize connection
  useEffect(() => {
    if (!initialized || !keycloak.authenticated) return;
    if (connectionInitialized) return; // Already initialized

    console.log('NotificationWebSocketProvider: Setting up notification WebSocket listeners');
    
    // Handle new notifications
    const handleNewNotification = (notification: NotificationData) => {
      console.log('NotificationWebSocketProvider: New notification received:', notification);
      setNotifications(prev => {
        // Check if notification already exists
        const exists = prev.some(n => n.id === notification.id);
        if (exists) {
          return prev; // Don't add duplicates
        }
        return [notification, ...prev];
      });
    };

    // Handle notification read confirmation
    const handleNotificationRead = (data: { notificationId: string; readAt: string }) => {
      console.log('NotificationWebSocketProvider: Notification marked as read:', data);
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === data.notificationId 
            ? { ...notif, readAt: data.readAt }
            : notif
        )
      );
    };

  // Handle connection state changes
  const handleStateChange = (state: NotificationConnectionState) => {
    console.log('NotificationWebSocketProvider: Connection state changed:', state);
    setConnectionState(state);
    
    // When connection is established, request recent notifications
    if (state === NotificationConnectionState.CONNECTED) {
      // Add a small delay to ensure join is complete before requesting notifications
      setTimeout(() => {
        notificationSocketService.getNotifications(20, 0);
      }, 1000);
    }
  };

    // Set up listeners following backend documentation
    notificationSocketService.onNotification(handleNewNotification);
    notificationSocketService.onNotificationRead(handleNotificationRead);
    notificationSocketService.onNotificationsHistory((data: any) => {
      console.log('NotificationWebSocketProvider: History received:', data);
      setNotifications(data.notifications || []);
    });
    notificationSocketService.onStateChange(handleStateChange);
    
    // Set the connection as initialized (listeners are set up)
    setConnectionInitialized(true);

    // Clean up listeners on unmount - but don't disconnect
    return () => {
      notificationSocketService.removeStateChangeListener(handleStateChange);
    };
  }, [initialized, keycloak.authenticated]);

  // Handle connection when context becomes available
  useEffect(() => {
    if (!connectionInitialized || !currentOrganization?.id) {
      return;
    }

    // Check if we're already connected or connecting
    if (notificationSocketService.isConnected() || notificationSocketService.getState() === NotificationConnectionState.CONNECTING) {
      return;
    }

    console.log('NotificationWebSocketProvider: Context available, connecting...', {
      org: currentOrganization.id
    });

    notificationSocketService.connect();
  }, [connectionInitialized, currentOrganization?.id]);

  // Set up authentication change handler
  useEffect(() => {
    if (!initialized) return;

    // If user logs out, disconnect WebSocket and clear notifications
    if (!keycloak.authenticated && connectionInitialized) {
      console.log('NotificationWebSocketProvider: User logged out, disconnecting WebSocket');
      notificationSocketService.disconnect();
      setNotifications([]);
      setConnectionInitialized(false);
    }
  }, [keycloak.authenticated, initialized, connectionInitialized]);

  // Public API methods
  const connect = async (): Promise<void> => {
    await notificationSocketService.connect();
  };

  const forceReconnect = async (): Promise<void> => {
    console.log('NotificationWebSocketProvider: Force reconnecting...');
    setNotifications([]); // Clear notifications, they'll be fetched on reconnect
    await notificationSocketService.forceReconnect();
  };

  const getConnectionInfo = (): ConnectionInfo => {
    return notificationSocketService.getConnectionInfo();
  };

  const markAsRead = (notificationId: string): boolean => {
    // Optimistically update UI
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId 
          ? { ...notif, readAt: new Date().toISOString() }
          : notif
      )
    );
    
    // Send to server
    return notificationSocketService.markAsRead(notificationId);
  };

  const markAllAsRead = (): void => {
    const now = new Date().toISOString();
    
    // Optimistically update UI
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, readAt: notif.readAt || now }))
    );
    
    // Send individual mark read requests for unread notifications
    notifications
      .filter(n => !n.readAt)
      .forEach(n => notificationSocketService.markAsRead(n.id));
  };

  const getNotifications = (limit = 20, offset = 0): boolean => {
    return notificationSocketService.getNotifications(limit, offset);
  };

  const clearNotifications = (): void => {
    setNotifications([]);
  };

  const contextValue: NotificationWebSocketContextType = {
    isConnected,
    connectionState,
    notifications,
    unreadCount,
    connect,
    forceReconnect,
    markAsRead,
    markAllAsRead,
    getNotifications,
    clearNotifications,
    getConnectionInfo,
  };

  return (
    <NotificationWebSocketContext.Provider value={contextValue}>
      {children}
    </NotificationWebSocketContext.Provider>
  );
};

// Hook to use notification WebSocket context
export const useNotificationWebSocket = (): NotificationWebSocketContextType => {
  const context = useContext(NotificationWebSocketContext);
  if (context === undefined) {
    throw new Error('useNotificationWebSocket must be used within a NotificationWebSocketProvider');
  }
  return context;
};
